/*eslint max-lines-per-function: ["error", 200]*/
/*eslint complexity: ["error", 33]*/
import React, { useState } from 'react';
import { Accordion, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { TFunction } from 'i18next';

import {
  CreateUpdateEstimateItemDto,
  GetEstimateByMonthDto,
  GetEstimateItemDto,
  GetPositionExtendedDto,
  GetProjectDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import {
  convert,
  validate,
} from '../../../common/pokCore/contexts/EstimateItemContext';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import numberFormatter from '../../../common/numberFormatter';
import mathUtils from '../../../utils/mathUtils';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import { PositionTypeEnum } from '../../../common/pokCore/validation/enums';

import { EstimateItemTabs } from './EstimateItemTabs';

interface EstimateItemAccordionProps {
  estimateItem: GetEstimateItemDto;
  estimateByMonth?: GetEstimateByMonthDto;
  project?: GetProjectDto;
  idx: string;
  activeItemIdx?: string;
  refresh(): void;
  handleOnClick: () => void;
  onDelete: (id?: string) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
  cloneItem: (id: string, withAmounts: boolean) => () => void;
  closeAccordion: () => void;
  salesBlocked: boolean;
  purchaseBlocked: boolean;
}

const renderPositionPhaseBadge = (
  estimateItem: GetEstimateItemDto,
  lang: string,
  t: TFunction<'projectLng', 'projectLng'>,
  tk: TKeys<'projectLng'>,
) => {
  const phaseLabel =
    lang === 'PL'
      ? estimateItem?.pathPhaseDictionary?.value
      : estimateItem?.pathPhaseDictionary?.valueEn ||
        estimateItem?.pathPhaseDictionary?.value;

  if (!phaseLabel) {
    return null;
  }

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{t(tk.common.positionPhase)}</Tooltip>}
    >
      <Badge className="ms-2 px-2 py-1" pill={true}>
        {phaseLabel}
      </Badge>
    </OverlayTrigger>
  );
};

const generateTitle = (
  estimateItem: GetEstimateItemDto,
  showMargin: boolean,
  lang: string,
  t: TFunction<'projectLng', 'projectLng'>,
  tk: TKeys<'projectLng'>,
) => {
  return (
    <section className="layout-accordion-header">
      <div className="grow1">
        <b>{estimateItem?.position?.name} </b>
        {estimateItem?.position.client.shortname
          ? `(${estimateItem?.position.client.shortname}) `
          : ''}
        {estimateItem?.service ? `${estimateItem?.service}` : ''}
        {estimateItem?.orderNumber
          ? `${t(tk.estimates.orderNumber)}: ${estimateItem?.orderNumber}`
          : ''}
        {renderPositionPhaseBadge(estimateItem, lang, t, tk)}
        {estimateItem?.position.positionType ===
          PositionTypeEnum.TechnicalCost && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{t(tk.enum.technicalCost)}</Tooltip>}
          >
            <Badge className="ms-2 px-2 py-1" pill={true}>
              {t(tk.estimates.technicalCostAbbreviation)}
            </Badge>
          </OverlayTrigger>
        )}
        {estimateItem?.position.positionType ===
          PositionTypeEnum.Commission && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{t(tk.enum.commission)}</Tooltip>}
          >
            <Badge className="ms-2 px-2 py-1" pill={true}>
              {t(tk.estimates.commissionAbbreviation)}
            </Badge>
          </OverlayTrigger>
        )}
        {estimateItem?.position.positionType ===
          PositionTypeEnum.TransferredCommission && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{t(tk.enum.transferredCommission)}</Tooltip>}
          >
            <Badge className="ms-2 px-2 py-1" pill={true}>
              {t(tk.estimates.transferredCommissionAbbreviation)}
            </Badge>
          </OverlayTrigger>
        )}
      </div>
      <div className="marginLeft">
        {`${t(tk.estimates.sales)}: `}
        <b>{numberFormatter(estimateItem?.salesNetTotal || '0', true)}</b>{' '}
      </div>
      <div className="marginLeft">
        {`${t(tk.estimates.purchase)}: `}
        <b>
          {numberFormatter(estimateItem?.purchaseNetTotal || '0', true)}
        </b>{' '}
      </div>
      <div className="marginLeft">
        {`${t(tk.estimates.fvz)}: `}
        <b>
          {numberFormatter(estimateItem?.purchaseInvoicesSum || '0', true)}
        </b>{' '}
      </div>
      <div className="marginLeft">
        {`${t(tk.estimates.fvzDiff)}: `}
        <b>
          {numberFormatter(
            mathUtils.subtract(
              estimateItem?.purchaseNetTotal || 0,
              estimateItem?.purchaseInvoicesSum || 0,
            ),
            true,
          )}
        </b>{' '}
      </div>
      {showMargin && (
        <div className="marginLeft">
          {`${t(tk.estimates.margin)}: `}
          <b>
            {numberFormatter(
              mathUtils.subtract(
                estimateItem?.salesNetTotal || 0,
                estimateItem?.purchaseNetTotal || 0,
              ),
              true,
            )}
          </b>{' '}
        </div>
      )}
    </section>
  );
};

export const EstimateItemAccordion: React.FC<
  EstimateItemAccordionProps
> = props => {
  const showMargin = !!props.project?.purchaser?.company?.showMargin;

  const [estimateItem, setEstimateItem] = useState<GetEstimateItemDto>(
    props.estimateItem,
  );
  const pok = usePokCore();
  const lang = pok.getLanguage();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('projectLng');

  const createAddEstimateItem = async (
    item: CreateUpdateEstimateItemDto,
    id?: string,
    noOrderRequired?: boolean,
    position?: GetPositionExtendedDto,
  ) => {
    const status = await validate(
      t,
      tk,
      item,
      lang,
      position,
      props.estimateByMonth?.profile,
      props.salesBlocked,
      props.purchaseBlocked,
      props.project,
    );
    if (status.valid) {
      try {
        let savedId = id;
        if (id) {
          const updatedItem = await pok.estimateItems.update(id, item);
          setEstimateItem(updatedItem);
        } else {
          const newItem = await pok.estimateItems.create(item);
          savedId = newItem.id;
          setEstimateItem(newItem);
        }
        if (savedId && noOrderRequired !== undefined) {
          await pok.stepDefinitions.setNoOrderRequired(
            savedId,
            noOrderRequired,
          );
        }
        notifications.saveCompleted();
        props.refresh();
        return {
          saved: true,
          errors: [],
          amountFieldsValid: true,
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
          amountFieldsValid: false,
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
        amountFieldsValid: status.amountFieldsValid,
      };
    }
  };

  return (
    <Accordion.Item
      eventKey={props.idx}
      className={`flex-fill ${
        props.activeItemIdx === props.idx ? 'active-item-accordion' : ''
      }`}
    >
      <Accordion.Header
        onClick={props.handleOnClick}
        className={`${estimateItem?.id ? '' : 'new-item-accordion'}`}
      >
        {estimateItem?.id
          ? generateTitle(estimateItem, showMargin, lang, t, tk)
          : t(tk.estimates.newUnwritten)}
      </Accordion.Header>
      <Accordion.Body>
        {props.activeItemIdx === props.idx && (
          <EstimateItemTabs
            id={estimateItem?.id}
            positionName={estimateItem?.position?.name}
            estimateItem={convert(
              props.estimateItem,
              props.estimateByMonth,
              props.project,
            )}
            isSalesBlocked={props.salesBlocked}
            isPurchaseBlocked={props.purchaseBlocked}
            profile={props.estimateByMonth?.profile}
            project={props.project}
            onCompleted={createAddEstimateItem}
            onDelete={props.onDelete}
            cloneItem={props.cloneItem}
            closeAccordion={props.closeAccordion}
          />
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
};
